/**
 * SentPostsTab — "Sent" view inside My Posts. Lists successfully PUBLISHED
 * posts (status='published', regardless of their auto-archived flag) and gives
 * each a click-through to the live post on the platform, lightweight engagement
 * stats (likes/comments/shares where the platform API allows), copy/share, and
 * delete / bulk-delete. Sortable + filterable by platform and date.
 *
 * Pairs with backend:
 *   GET    /api/posts?status=published        → published posts
 *   POST   /api/posts/{id}/engagement         → refresh one post's stats
 *   POST   /api/posts/engagement/refresh      → refresh many (<=50)
 *   DELETE /api/posts/{id}                     → delete one
 *   POST   /api/posts/bulk {action:'delete'}   → bulk delete
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useClients } from '../hooks/useClients';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Send, Loader2, RefreshCw, Search, ExternalLink, CheckCircle2, LinkIcon,
  Heart, MessageCircle, Share2, Copy, Trash2, CheckSquare, BarChart3, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_META = {
  linkedin:  { label: 'LinkedIn',  color: 'bg-sky-100 text-sky-800 border-sky-200' },
  facebook:  { label: 'Facebook',  color: 'bg-blue-100 text-blue-800 border-blue-200' },
  instagram: { label: 'Instagram', color: 'bg-pink-100 text-pink-800 border-pink-200' },
  bluesky:   { label: 'Bluesky',   color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  youtube:   { label: 'YouTube',   color: 'bg-red-100 text-red-800 border-red-200' },
  tiktok:    { label: 'TikTok',    color: 'bg-zinc-200 text-zinc-800 border-zinc-300' },
  x_twitter: { label: 'X',         color: 'bg-zinc-200 text-zinc-800 border-zinc-300' },
  twitter:   { label: 'X',         color: 'bg-zinc-200 text-zinc-800 border-zinc-300' },
};

const platformKey = (p) => String(p || '').toLowerCase().replace(/\s+/g, '_');
const platformLabel = (p) => PLATFORM_META[platformKey(p)]?.label || (p || 'Platform');

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return '—'; }
};

const heroImage = (post) => {
  const m = (post.media || []).find((x) => x.type === 'image' && x.url);
  return m?.url || null;
};

const publishedTime = (p) => new Date(p.published_at || p.created_at).getTime();

export const SentPostsTab = () => {
  const { api } = useAuth();
  const { byId: clientsById } = useClients();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/posts?status=published&limit=200');
      setPosts(r.data?.posts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load sent posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const platformCounts = useMemo(() => {
    const out = { all: posts.length };
    for (const p of posts) {
      const k = platformKey(p.platform);
      out[k] = (out[k] || 0) + 1;
    }
    return out;
  }, [posts]);

  const presentPlatforms = useMemo(
    () => Object.keys(platformCounts).filter((k) => k !== 'all'),
    [platformCounts],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return posts
      .filter((p) => platformFilter === 'all' ? true : platformKey(p.platform) === platformFilter)
      .filter((p) => {
        if (!q) return true;
        return (
          (p.post_title || '').toLowerCase().includes(q) ||
          (p.caption || '').toLowerCase().includes(q) ||
          (clientsById?.[p.client_id]?.name || '').toLowerCase().includes(q)
        );
      })
      .filter((p) => {
        const t = publishedTime(p);
        if (fromTs && t < fromTs) return false;
        if (toTs && t > toTs) return false;
        return true;
      })
      .sort((a, b) => sortOrder === 'newest'
        ? publishedTime(b) - publishedTime(a)
        : publishedTime(a) - publishedTime(b));
  }, [posts, platformFilter, search, clientsById, dateFrom, dateTo, sortOrder]);

  const applyEngagement = (id, engagement) =>
    setPosts((prev) => prev.map((p) => (p.id === id ? { ...p, engagement } : p)));

  const refreshOne = async (post) => {
    setRefreshingId(post.id);
    try {
      const r = await api.post(`/posts/${post.id}/engagement`);
      applyEngagement(post.id, r.data.engagement);
      const e = r.data.engagement;
      if (e?.supported) toast.success('Stats updated');
      else toast.message(`Stats unavailable for ${platformLabel(post.platform)} (reconnect the account or unsupported)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refresh stats');
    } finally {
      setRefreshingId(null);
    }
  };

  const refreshAll = async () => {
    const ids = visible.map((p) => p.id).slice(0, 50);
    if (ids.length === 0) return;
    setRefreshingAll(true);
    try {
      const r = await api.post('/posts/engagement/refresh', { post_ids: ids });
      const map = r.data?.engagement || {};
      setPosts((prev) => prev.map((p) => (map[p.id] ? { ...p, engagement: map[p.id] } : p)));
      const supported = Object.values(map).filter((e) => e?.supported).length;
      toast.success(`Refreshed ${r.data.refreshed} post${r.data.refreshed === 1 ? '' : 's'} — ${supported} with live stats`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk refresh failed');
    } finally {
      setRefreshingAll(false);
    }
  };

  const copyLink = async (post) => {
    if (!post.published_url) return;
    try {
      await navigator.clipboard.writeText(post.published_url);
      toast.success('Live post link copied');
    } catch {
      toast.error('Could not copy — open the post and copy manually');
    }
  };

  const sharePost = async (post) => {
    if (!post.published_url) return;
    const shareData = {
      title: post.post_title || 'Check out my post',
      text: (post.caption || '').slice(0, 200),
      url: post.published_url,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      await copyLink(post);
    }
  };

  const deleteOne = async (post) => {
    if (!window.confirm('Delete this sent post from PostToSocial?\nThis only removes it here — the live post on the platform stays up.')) return;
    setDeletingId(post.id);
    try {
      await api.delete(`/posts/${post.id}`);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(post.id); return n; });
      toast.success('Post deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const selectAllVisible = () => setSelectedIds(new Set(visible.map((p) => p.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} sent post${ids.length === 1 ? '' : 's'} from PostToSocial?\nThe live posts on the platforms stay up.`)) return;
    setBulkDeleting(true);
    try {
      await api.post('/posts/bulk', { action: 'delete', post_ids: ids });
      setPosts((prev) => prev.filter((p) => !selectedIds.has(p.id)));
      clearSelection();
      setSelectMode(false);
      toast.success(`Deleted ${ids.length} post${ids.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const Stat = ({ icon: Icon, value }) => (
    <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
      <Icon className="w-3.5 h-3.5 text-zinc-400" />
      {typeof value === 'number' ? value.toLocaleString() : '—'}
    </span>
  );

  return (
    <div className="space-y-4" data-testid="sent-posts-tab">
      {/* Toolbar: platform chips + search + sort + dates + actions */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <button
            type="button"
            onClick={() => setPlatformFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              platformFilter === 'all' ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
            }`}
            data-testid="sent-posts-filter-all"
          >
            All <span className="ml-1.5 opacity-70">({platformCounts.all})</span>
          </button>
          {presentPlatforms.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setPlatformFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                platformFilter === k ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
              }`}
              data-testid={`sent-posts-filter-${k}`}
            >
              {platformLabel(k)} <span className="ml-1.5 opacity-70">({platformCounts[k]})</span>
            </button>
          ))}
          <div className="flex-1 min-w-[160px] sm:max-w-xs relative">
            <Search className="w-3.5 h-3.5 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title / caption / client…"
              className="pl-8 h-9 text-sm"
              data-testid="sent-posts-search"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="sent-posts-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
            <span>From</span>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-[150px] text-xs" data-testid="sent-posts-date-from" />
            <span>to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-[150px] text-xs" data-testid="sent-posts-date-to" />
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={() => { setDateFrom(''); setDateTo(''); }} data-testid="sent-posts-clear-dates">
                Clear
              </Button>
            )}
          </div>

          <div className="ml-auto flex flex-wrap gap-1.5">
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshingAll || loading || visible.length === 0} title={visible.length > 50 ? 'Refreshes the first 50 posts at a time' : 'Refresh engagement stats'} data-testid="sent-posts-refresh-stats">
              {refreshingAll ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BarChart3 className="w-3.5 h-3.5 mr-1.5" />}
              Refresh stats
            </Button>
            <Button
              variant={selectMode ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => { setSelectMode((v) => !v); clearSelection(); }}
              data-testid="sent-posts-select-toggle"
            >
              <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
              {selectMode ? 'Done' : 'Select'}
            </Button>
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading} data-testid="sent-posts-refresh">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </div>

        {selectMode && (
          <div className="flex flex-wrap items-center gap-2 rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2">
            <span className="text-xs text-zinc-600" data-testid="sent-posts-selected-count">{selectedIds.size} selected</span>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAllVisible} data-testid="sent-posts-select-all">Select all ({visible.length})</Button>
            {selectedIds.size > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearSelection}>Clear</Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              className="h-7 text-xs ml-auto"
              onClick={bulkDelete}
              disabled={selectedIds.size === 0 || bulkDeleting}
              data-testid="sent-posts-bulk-delete"
            >
              {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Delete selected ({selectedIds.size})
            </Button>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-zinc-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {!loading && visible.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center space-y-2">
            <Send className="w-10 h-10 text-zinc-300 mx-auto" />
            <p className="font-medium text-zinc-700">
              {posts.length === 0 ? 'No sent posts yet' : 'No sent posts match these filters'}
            </p>
            <p className="text-xs text-zinc-500 max-w-md mx-auto">
              {posts.length === 0
                ? 'Once a post is published to a platform it appears here with a link to the live post and engagement stats.'
                : 'Try a different platform, date range, or clear the search.'}
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && visible.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.map((post) => {
            const meta = PLATFORM_META[platformKey(post.platform)] || {};
            const client = clientsById?.[post.client_id];
            const img = heroImage(post);
            const hasLink = !!post.published_url;
            const eng = post.engagement;
            const hasStats = eng?.supported && (typeof eng.likes === 'number' || typeof eng.comments === 'number');
            const checked = selectedIds.has(post.id);
            return (
              <Card key={post.id} className={`hover:border-zinc-300 transition-colors flex flex-col ${checked ? 'ring-2 ring-indigo-500' : ''}`} data-testid={`sent-post-card-${post.id}`}>
                <div className="relative">
                  {img && (
                    <div className="h-32 w-full overflow-hidden rounded-t-lg bg-zinc-100">
                      <img src={img} alt={post.post_title || 'post'} className="w-full h-full object-cover" />
                    </div>
                  )}
                  {selectMode && (
                    <div className="absolute top-2 left-2">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleSelect(post.id)}
                        className="bg-white border-zinc-300 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                        data-testid={`sent-post-select-${post.id}`}
                      />
                    </div>
                  )}
                </div>
                <CardContent className="p-4 space-y-2 flex-1 flex flex-col">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`gap-1 ${meta.color || 'bg-zinc-100 text-zinc-700 border-zinc-200'}`}>
                      {platformLabel(post.platform)}
                    </Badge>
                    <Badge variant="outline" className="gap-1 bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`sent-post-status-${post.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Published
                    </Badge>
                    {client && (
                      <Badge variant="secondary" className="text-[10px]">{client.name}</Badge>
                    )}
                  </div>
                  {post.post_title && (
                    <p className="font-semibold text-sm text-zinc-900 line-clamp-1" title={post.post_title}>{post.post_title}</p>
                  )}
                  <p className="text-xs text-zinc-600 line-clamp-3 flex-1">{post.caption}</p>

                  {/* Engagement row */}
                  <div className="flex items-center gap-3 pt-1" data-testid={`sent-post-engagement-${post.id}`}>
                    {hasStats ? (
                      <>
                        <Stat icon={Heart} value={eng.likes} />
                        <Stat icon={MessageCircle} value={eng.comments} />
                        {typeof eng.shares === 'number' && <Stat icon={Share2} value={eng.shares} />}
                      </>
                    ) : (
                      <span className="text-[11px] text-zinc-400">{eng ? 'Stats unavailable' : 'No stats yet'}</span>
                    )}
                    <button
                      type="button"
                      onClick={() => refreshOne(post)}
                      disabled={refreshingId === post.id}
                      className="ml-auto text-zinc-400 hover:text-indigo-600 transition-colors"
                      title="Refresh engagement stats"
                      data-testid={`sent-post-refresh-stats-${post.id}`}
                    >
                      {refreshingId === post.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 text-[11px] text-zinc-500" data-testid={`sent-post-sent-at-${post.id}`}>
                    <CalendarClock className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                    <span>Sent {formatDate(post.published_at)}</span>
                  </div>

                  {/* Primary action */}
                  {hasLink ? (
                    <Button
                      size="sm"
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => window.open(post.published_url, '_blank', 'noopener,noreferrer')}
                      data-testid={`sent-post-view-on-platform-${post.id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                      View on {platformLabel(post.platform)}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="w-full text-zinc-400 cursor-not-allowed" disabled title="The platform didn't return a direct link" data-testid={`sent-post-no-link-${post.id}`}>
                      <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> Published — link unavailable
                    </Button>
                  )}

                  {/* Secondary actions */}
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="flex-1 h-8 text-xs" onClick={() => copyLink(post)} disabled={!hasLink} data-testid={`sent-post-copy-${post.id}`}>
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                    </Button>
                    <Button variant="ghost" size="sm" className="flex-1 h-8 text-xs" onClick={() => sharePost(post)} disabled={!hasLink} data-testid={`sent-post-share-${post.id}`}>
                      <Share2 className="w-3.5 h-3.5 mr-1" /> Share
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs text-rose-600 hover:bg-rose-50"
                      onClick={() => deleteOne(post)}
                      disabled={deletingId === post.id}
                      data-testid={`sent-post-delete-${post.id}`}
                    >
                      {deletingId === post.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SentPostsTab;
